<template>
    <div class="order-detail">
        <OrderDetail
            :order="orderData"
            :section="'order_details'"
            :show-section="showSectionData('order_details')"
            :user-list-view="columns"
            @update-list="updateList"
        />
        <CustomerDetails
            :order="orderData"
            :section="'customer_details'"
            :show-section="showSectionData('customer_details')"
            :user-list-view="columns"
            @update-list="updateList"
            @unassign-customer="unassignCustomer(orderData)"
        />
        <ContactDetails
            v-if="orderData.client === 'PSA'"
            :order-contacts="orderData.orderContacts"
            :database-contacts="orderData.databaseContacts"
        />
        <FraudDetectionDetails
            :order="orderData"
            :section="'fraud_detection'"
            :show-section="showSectionData('fraud_detection')"
            :user-list-view="columns"
            :is-prepaid-order-with-invoicing-disabled="isPrepaidOrderWithInvoicingDisabled(orderData)"
            @update-list="updateList"
        />
        <ItemDetails
            :order="orderData"
            :section="'ordered_items'"
            :show-section="showSectionData('ordered_items')"
            :user-list-view="columns"
            @update-list="updateList"
        />
        <PaymentDetails
            :order="orderData"
            :section="'payment'"
            :show-section="showSectionData('payment')"
            :user-list-view="columns"
            @update-list="updateList"
        />
        <MarketingDetails
            :order="orderData"
            :errors="errors"
            :section="'marketing'"
            :show-section="showSectionData('marketing')"
            :user-list-view="columns"
            @update-list="updateList"
        />
        <SalesDetails
            :order="orderData"
            :section="'sales'"
            :show-section="showSectionData('sales')"
            :user-list-view="columns"
            :segments="segments"
            :selected-segments="selectedSegments"
            @update-list="updateList"
        />
        <OrderPersonDetails
            :order="orderData"
            :section="'order_person_details'"
            :show-section="showSectionData('order_person_details')"
            :user-list-view="columns"
            @update-list="updateList"
        />
        <RawDataDetails
            :order="orderData"
            :section="'raw_data'"
            :show-section="showSectionData('raw_data')"
            :user-list-view="columns"
            @update-list="updateList"
        />
        <DFabs v-if="orderData.client === 'PSA'">
            <DFab
                v-if="userPermissions.includes('order.view')"
                icon="cloud-check-variant-outline"
                :label="__('orders.check.check')"
                @click="checkPsaOrder(orderData)"
            />
        </DFabs>
        <DFabs v-if="orderData.client === 'OneWebshop'">
            <DFab
                v-if="orderData.valid && userPermissions.includes('order.accept') && orderData.status === 'open' && orderData.databaseCustomerId === null"
                icon="pencil"
                :label="__('orders.check_new_customer_data')"
                @click="checkNewCustomerData(orderData)"
            />
            <DFab
                v-if="orderData.valid && userPermissions.includes('order.accept') && orderData.status === 'open' && orderData.databaseCustomerId !== null"
                icon="check-circle"
                :label="__('orders.accept')"
                @click="acceptOneWebshopOrder(orderData)"
            />
            <DFab
                v-if="orderData.status === 'open' && !orderData.customer?.id && (orderData.databaseCustomers && orderData.databaseCustomers.length > 0)"
                icon="clipboard-account"
                :label="__('orders.title.assign_customer')"
                @click="displayOneWebshopAssignCustomerDialog(orderData)"
            />
            <DFab
                v-if="userPermissions.includes('order.reject') && orderData.status === 'open'"
                icon="close-circle"
                :label="__('orders.reject')"
                @click="rejectOneWebshopOrder(orderData, false)"
            />
            <DFab
                v-if="userPermissions.includes('order.reject') && orderData.status === 'open'"
                icon="close-circle-multiple-outline"
                :label="__('orders.silent_reject')"
                @click="rejectOneWebshopOrder(orderData, true)"
            />
            <DFab
                icon="note-edit"
                :label="__('orders.note.label')"
                @click="showOrderNoteDialog(orderData)"
            />
        </DFabs>
        <DFabs v-if="orderData.client === 'Webglobe'">
            <DFab
                v-if="orderData.valid && userPermissions.includes('order.accept') && orderData.status === 'open' && orderData.databaseCustomerId === null"
                icon="pencil"
                :label="__('orders.check_new_customer_data')"
                @click="checkNewCustomerData(orderData)"
            />
            <DFab
                v-if="orderData.valid && userPermissions.includes('order.accept') && orderData.status === 'open' && orderData.databaseCustomerId !== null"
                icon="check-circle"
                :label="__('orders.accept')"
                @click="acceptWebglobeOrder(orderData)"
            />
            <DFab
                v-if="orderData.status === 'open' && !orderData.customer?.id && (orderData.databaseCustomers && orderData.databaseCustomers.length > 0)"
                icon="clipboard-account"
                :label="__('orders.title.assign_customer')"
                @click="displayWebglobeAssignCustomerDialog(orderData)"
            />
            <DFab
                v-if="userPermissions.includes('order.reject') && orderData.status === 'open'"
                icon="close-circle"
                :label="__('orders.reject')"
                @click="rejectWebglobeOrder(orderData, false)"
            />
            <DFab
                v-if="userPermissions.includes('order.reject') && orderData.status === 'open'"
                icon="close-circle-multiple-outline"
                :label="__('orders.silent_reject')"
                @click="rejectWebglobeOrder(orderData, true)"
            />
        </DFabs>
        <DFabs v-if="orderData.client === 'OSA'">
            <DFab
                v-if="itemOsaAcceptable(orderData) && userPermissions.includes('order.accept')"
                icon="check-circle"
                :label="__('orders.accept')"
                @click="acceptOsaOrder(orderData, useBanktransfer)"
            />
            <DFab
                v-if="orderData.status === 'open' && (!orderData.customer || !orderData.customer.id) && (orderData.databaseCustomers && orderData.databaseCustomers.length > 0)"
                icon="clipboard-account"
                :label="__('orders.title.assign_customer')"
                @click="displayOsaAssignCustomerDialog(orderData)"
            />
            <DFab
                v-if="userPermissions.includes('order.reject')"
                icon="close-circle"
                :label="__('orders.reject')"
                @click="rejectOsaOrder(orderData, false)"
            />
            <DFab
                v-if="userPermissions.includes('order.reject')"
                icon="close-circle-multiple-outline"
                :label="__('orders.silent_reject')"
                @click="rejectOsaOrder(orderData, true)"
            />
            <DFab
                icon="note-edit"
                :label="__('orders.note.label')"
                @click="showOrderNoteDialog(orderData)"
            />
        </DFabs>
        <DFabs v-if="orderData.client === 'WooCommerce'">
            <DFab
                v-if="itemWooCommerceAcceptable(orderData) && userPermissions.includes('order.accept')"
                icon="check-circle"
                :label="__('orders.accept')"
                @click="prepareWooCommerceDataForOrderAccept(orderData)"
            />
            <DFab
                v-if="userPermissions.includes('order.reject')"
                icon="close-circle"
                :label="__('orders.reject')"
                @click="rejectWooCommerceOrder(orderData, false)"
            />
            <DFab
                v-if="userPermissions.includes('order.reject')"
                icon="close-circle-multiple-outline"
                :label="__('orders.silent_reject')"
                @click="rejectWooCommerceOrder(orderData, true)"
            />
            <DFab
                icon="note-edit"
                :label="__('orders.note.label')"
                @click="showOrderNoteDialog(orderData)"
            />
        </DFabs>
        <DModal
            v-model:show="showOsaRejectDialog"
            :title="dialogTitle"
            :message="dialogMessage"
            is-confirm
            is-error
            @close="showOsaRejectDialog = false"
            @confirm="rejectOsaCallback"
        />
        <DModal
            v-model:show="showOsaAcceptDialog"
            :title="dialogTitle"
            :message="dialogMessage"
            is-confirm
            @close="showOsaAcceptDialog = false"
            @confirm="acceptOsaCallback"
        />
        <CustomerAssignDialogModal
            v-if="showOsaCustomerAssignDialog"
            :show-modal="showOsaCustomerAssignDialog"
            :selected-item="selectedItem"
            :selected-database-customer="selectedOsaDatabaseCustomer"
            :order-customer="orderCustomer"
            :database-customers="databaseCustomers"
            @confirm-modal="assignOsaCustomerToOrder"
            @close-modal="showOsaCustomerAssignDialog = false"
        />
        <OrderNoteCreateModal
            v-if="showNoteCreateModal"
            :show-modal="showNoteCreateModal"
            :title="`${__('orders.note.title')} ${selectedItem.orderNumber}`"
            :order-note="orderNoteDialog.currentNote"
            :errors="$page.props.errors"
            @close="showNoteCreateModal = false"
            @confirm="saveOrderNote"
        />
        <DModal
            v-model:show="showOneWebshopRejectDialog"
            :title="dialogTitle"
            :message="dialogMessage"
            is-confirm
            is-error
            @close="showOneWebshopRejectDialog = false"
            @confirm="rejectOneWebshopCallback"
        />
        <DModal
            v-model:show="showOneWebshopAcceptDialog"
            :title="dialogTitle"
            :message="dialogMessage"
            is-confirm
            @close="showOneWebshopAcceptDialog = false"
            @confirm="acceptOneWebshopCallback"
        />
        <DModal
            v-model:show="showWebglobeRejectDialog"
            :title="dialogTitle"
            :message="dialogMessage"
            is-confirm
            is-error
            @close="showWebglobeRejectDialog = false"
            @confirm="rejectWebglobeCallback"
        />
        <DModal
            v-model:show="showWebglobeAcceptDialog"
            :title="dialogTitle"
            :message="dialogMessage"
            is-confirm
            @close="showWebglobeAcceptDialog = false"
            @confirm="acceptWebglobeCallback"
        />
        <DModal
            v-model:show="showWooCommerceAcceptDialog"
            :title="dialogTitle"
            :message="dialogMessage"
            is-confirm
            @close="showWooCommerceAcceptDialog = false"
            @confirm="acceptWooCommerceCallback"
        />
        <DModal
            v-model:show="showWooCommerceRejectDialog"
            :title="dialogTitle"
            :message="dialogMessage"
            is-confirm
            is-error
            @close="showWooCommerceRejectDialog = false"
            @confirm="rejectWooCommerceCallback"
        />
        <DModal
            v-model:show="showAcceptInvoiceDisabledDialog"
            :title="__('orders.invoice_disabled_dialog.title')"
            :message="__('orders.invoice_disabled_dialog.message')"
            is-error
            @close="showAcceptInvoiceDisabledDialog = false"
        />
        <AssignCustomerDialogModal
            v-if="showWooCommerceAssignCustomerDialog"
            :show-modal="showWooCommerceAssignCustomerDialog"
            :selected-item="selectedItem"
            :database-customers="databaseCustomers"
            @confirm-modal="assignWooCommerceCustomerAndAccept"
            @close-modal="showWooCommerceAssignCustomerDialog = false"
        />
        <DomainOwnerInfoModal
            v-if="showDomainsModal"
            :show-modal="showDomainsModal"
            :domains="orderData.domains"
            :billing-comments="orderData.billingComments"
            :errors="domainErrors"
            @confirm="getDomainOwnerDetails"
            @closeModal="closeDomainsModal"
        />
        <CustomerAssignDialogModal
            v-if="showOneWebshopCustomerAssignDialog"
            :show-modal="showOneWebshopCustomerAssignDialog"
            :selected-item="selectedItem"
            :selected-database-customer="selectedOneWebshopDatabaseCustomer"
            :order-customer="orderCustomer"
            :database-customers="databaseCustomers"
            :is-one-webshop-order="true"
            @confirm-modal="assignOneWebshopCustomerToOrder"
            @close-modal="showOneWebshopCustomerAssignDialog = false"
        />
        <CustomerAssignDialogModal
            v-if="showWebglobeCustomerAssignDialog"
            :show-modal="showWebglobeCustomerAssignDialog"
            :selected-item="selectedItem"
            :selected-database-customer="selectedWebglobeDatabaseCustomer"
            :order-customer="orderCustomer"
            :database-customers="databaseCustomers"
            :is-one-webshop-order="true"
            @confirm-modal="assignWebglobeCustomerToOrder"
            @close-modal="showWebglobeCustomerAssignDialog = false"
        />
        <DModal
            v-model:show="showRemoveAssignmentDialog"
            :title="__('orders.show.remove_customer_assignment_modal.title')"
            :message="__('orders.show.remove_customer_assignment_modal.message')"
            is-confirm
            @close="showRemoveAssignmentDialog = false"
            @confirm="removeCustomerAssignmentCallback"
        />
    </div>
</template>

<script setup>
import OrderNoteCreateModal from '~@/Components/Orders/OrderNoteCreateModal'
import { computed, ref } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'
import { customerBankDetails as getCustomerBankDetails } from '~@/Composables/sepaMandate'
import OrderDetail from '~@/Pages/Orders/Show/OrderDetails'
import CustomerDetails from '~@/Pages/Orders/Show/CustomerDetails'
import FraudDetectionDetails from '~@/Pages/Orders/Show/FraudDetectionDetails'
import ItemDetails from '~@/Pages/Orders/Show/ItemDetails'
import PaymentDetails from '~@/Pages/Orders/Show/PaymentDetails'
import MarketingDetails from '~@/Pages/Orders/Show/MarketingDetails'
import SalesDetails from '~@/Pages/Orders/Show/SalesDetails'
import RawDataDetails from '~@/Pages/Orders/Show/RawDataDetails'
import OrderPersonDetails from '~@/Pages/Orders/Show/OrderPersonDetails'
import CustomerAssignDialogModal from '~@/Components/Orders/CustomerAssignDialogModal'
import AssignCustomerDialogModal from '~@/Components/Orders/WooCommerce/AssignCustomerDialogModal'
import DomainOwnerInfoModal from '~@/Components/Orders/WooCommerce/DomainOwnerInfoModal'
import ContactDetails from '~@/Pages/Orders/Show/ContactDetails.vue'

const props = defineProps({
    orderData: {
        type: Object,
        default () {
            return {}
        }
    },
    page: {
        type: Number,
        default: 1
    },
    errors: {
        type: Object,
        default () {
            return {}
        }
    },
    userListView: {
        type: Array,
        default: () => {
            return []
        }
    },
    segments: {
        type: Array,
        default () {
            return []
        }
    },
    selectedSegments: {
        type: Array,
        default () {
            return []
        }
    }
})

const userPermissions = computed(() => usePage().props.auth.permissions)

// Modals
const dialogTitle = ref('')
const dialogMessage = ref('')

// PSA
// Remove customer assignment
const showRemoveAssignmentDialog = ref(false)
const removeCustomerAssignmentCallback = ref(() => { return {} })

// OneWebshop
// Accept order
const showOneWebshopAcceptDialog = ref(false)
const acceptOneWebshopCallback = ref(() => { return {} })
const showAcceptInvoiceDisabledDialog = ref(false)

const acceptOneWebshopOrder = (order) => {
    const orderNumber = order.orderNumber
    if (isPrepaidOrderWithInvoicingDisabled(order)) {
        showAcceptInvoiceDisabledDialog.value = true
        return
    }
    dialogTitle.value = __('orders.accept_order_dialog_title', { orderNumber })
    dialogMessage.value = __('orders.accept_dialog_message', { orderNumber })

    acceptOneWebshopCallback.value = () => {
        router.patch(
            route(
                'orders.one_webshop.accept', {
                    orderUuid: orderNumber
                }
            )
        )
    }

    showOneWebshopAcceptDialog.value = true
}

const unassignCustomer = (orderData) => {
    removeCustomerAssignmentCallback.value = () => {
        router.patch(
            route(
                'orders.psa.unassign_customer', {
                    order_id: orderData.idInternal
                }
            )
        )
    }

    showRemoveAssignmentDialog.value = true
}

const checkNewCustomerData = (order) => {
    return router.get(route('orders.edit-customer-details', { client: order.client, order: order.orderNumber }))
}

// Reject order
const showOneWebshopRejectDialog = ref(false)
const rejectOneWebshopCallback = ref(() => { return {} })

const rejectOneWebshopOrder = (order, silent) => {
    const orderNumber = order.orderNumber
    dialogTitle.value = __('orders.reject_order_dialog_title', { orderNumber })
    dialogMessage.value = __('orders.reject_dialog_message', { orderNumber })

    rejectOneWebshopCallback.value = () => {
        router.patch(route(
            'orders.one_webshop.reject',
            {
                orderUuid: orderNumber
            }
        ), { silent: silent })
    }

    showOneWebshopRejectDialog.value = true
}

// Webglobe
// Accept order
const showWebglobeAcceptDialog = ref(false)
const acceptWebglobeCallback = ref(() => { return {} })

const acceptWebglobeOrder = (order) => {
    const orderNumber = order.orderNumber
    if (isPrepaidOrderWithInvoicingDisabled(order)) {
        showAcceptInvoiceDisabledDialog.value = true
        return
    }
    dialogTitle.value = __('orders.accept_order_dialog_title', { orderNumber })
    dialogMessage.value = __('orders.accept_dialog_message', { orderNumber })

    acceptWebglobeCallback.value = () => {
        router.patch(
            route(
                'orders.webglobe.accept', {
                    orderUuid: orderNumber
                }
            )
        )
    }

    showWebglobeAcceptDialog.value = true
}

// Reject order
const showWebglobeRejectDialog = ref(false)
const rejectWebglobeCallback = ref(() => { return {} })

const rejectWebglobeOrder = (order, silent) => {
    const orderNumber = order.orderNumber
    dialogTitle.value = __('orders.reject_order_dialog_title', { orderNumber })
    dialogMessage.value = __('orders.reject_dialog_message', { orderNumber })

    rejectWebglobeCallback.value = () => {
        router.patch(route(
            'orders.webglobe.reject',
            {
                orderUuid: orderNumber
            }
        ), { silent: silent })
    }

    showWebglobeRejectDialog.value = true
}

// Assign customer

const showWebglobeCustomerAssignDialog = ref(false)
const selectedWebglobeDatabaseCustomer = ref(() => { return {} })

function displayWebglobeAssignCustomerDialog (item) {
    selectedItem.value = item
    orderCustomer.value = item.customer
    databaseCustomers.value = item.databaseCustomers
    selectedWebglobeDatabaseCustomer.value = item.databaseCustomers[0]
    selectedWebglobeDatabaseCustomer.value.bankDetails = getCustomerBankDetails(selectedWebglobeDatabaseCustomer.value)
    showWebglobeCustomerAssignDialog.value = true
}

// Osa
const useBanktransfer = computed(() => {
    return !!props.orderData.hasInvalidPaymentData
})

// Accept order
const showOsaAcceptDialog = ref(false)
const acceptOsaCallback = ref(() => { return {} })

const acceptOsaOrder = (order, switchToBanktransfer) => {
    const orderNumber = order.orderNumber
    dialogTitle.value = __('orders.accept_order_dialog_title', { orderNumber })
    dialogMessage.value = __('orders.accept_dialog_message', { orderNumber })

    acceptOsaCallback.value = () => {
        router.patch(
            route(
                'orders.osa.update', {
                    order: order.idExternal,
                    _query: {
                        page: props.page
                    }
                }
            ),
            { status: 'accepted', useDefaultPaymentMethod: switchToBanktransfer }
        )
    }

    showOsaAcceptDialog.value = true
}

// Reject order
const showOsaRejectDialog = ref(false)
const rejectOsaCallback = ref(() => { return {} })

const rejectOsaOrder = (order, silent) => {
    const orderNumber = order.orderNumber
    dialogTitle.value = __('orders.reject_order_dialog_title', { orderNumber })
    dialogMessage.value = __('orders.reject_dialog_message', { orderNumber })

    rejectOsaCallback.value = () => {
        if (order.valid) {
            router.patch(route('orders.osa.update', { order: order.idExternal, _query: { page: props.page, filter: order.status } }), { status: 'rejected', silent: silent })
        } else {
            router.patch(route('orders.osa.reject_invalid', { order: order.idExternal }), { status: 'rejected', silent: silent })
        }
    }

    showOsaRejectDialog.value = true
}

// Assign customer
const showOsaCustomerAssignDialog = ref(false)

function itemOsaAcceptable (item) {
    const acceptableStatuses = ['open', 'rejected']

    if (acceptableStatuses.includes(item.status) && item.valid) {
        return true
    }

    if (!item.valid && item.hasInvalidPaymentData) {
        return true
    }
}

const selectedOsaDatabaseCustomer = ref(() => { return {} })
const orderCustomer = ref(() => { return {} })

function displayOsaAssignCustomerDialog (item) {
    selectedItem.value = item
    orderCustomer.value = item.customer
    databaseCustomers.value = item.databaseCustomers
    selectedOsaDatabaseCustomer.value = item.databaseCustomers[0]
    selectedOsaDatabaseCustomer.value.bankDetails = getCustomerBankDetails(selectedOsaDatabaseCustomer.value)
    showOsaCustomerAssignDialog.value = true
}

const showOneWebshopCustomerAssignDialog = ref(false)
const selectedOneWebshopDatabaseCustomer = ref(() => { return {} })

function displayOneWebshopAssignCustomerDialog (item) {
    selectedItem.value = item
    orderCustomer.value = item.customer
    databaseCustomers.value = item.databaseCustomers
    selectedOneWebshopDatabaseCustomer.value = item.databaseCustomers[0]
    selectedOneWebshopDatabaseCustomer.value.bankDetails = getCustomerBankDetails(selectedOneWebshopDatabaseCustomer.value)
    showOneWebshopCustomerAssignDialog.value = true
}

function assignOsaCustomerToOrder (item) {
    router.post(
        route('orders.osa.assign_customer', {
            order: item.order.idExternal,
            customerId: item.customerId
        }),
        {
            customer_type: item.finalCustomer.customer_type,
            brand_id: item.finalCustomer.brand_id,
            title: item.finalCustomer.title,
            firstname: item.finalCustomer.firstname,
            lastname: item.finalCustomer.lastname,
            company: item.finalCustomer.company,
            address1: item.finalCustomer.address1,
            address2: item.finalCustomer.address2,
            postal_code: item.finalCustomer.postal_code,
            city: item.finalCustomer.city,
            country_code: item.finalCustomer.country_code,
            birthday: item.finalCustomer.birthday,
            phone: item.finalCustomer.phone,
            payment_method: item.finalCustomer.payment_method,
            payment_iban: item.finalCustomer.payment_iban,
            payment_bic: item.finalCustomer.payment_bic,
            account_owner: item.finalCustomer.account_owner,
            order_blocked: item.finalCustomer.order_blocked,
            order_blocked_reason: item.finalCustomer.order_blocked_reason
        }
    )

    showOsaCustomerAssignDialog.value = false
}

function assignOneWebshopCustomerToOrder (item) {
    router.post(
        route('orders.one_webshop.assign_customer', {
            orderUuid: item.order.orderNumber,
            customerId: item.customerId
        }),
        {
            customer_type: item.finalCustomer.customer_type,
            brand_id: item.finalCustomer.brand_id,
            title: item.finalCustomer.title,
            firstname: item.finalCustomer.firstname,
            lastname: item.finalCustomer.lastname,
            company: item.finalCustomer.company,
            address1: item.finalCustomer.address1,
            address2: item.finalCustomer.address2,
            postal_code: item.finalCustomer.postal_code,
            city: item.finalCustomer.city,
            country_code: item.finalCustomer.country_code,
            birthday: item.finalCustomer.birthday,
            phone: item.finalCustomer.phone,
            payment_method: item.finalCustomer.payment_method,
            payment_iban: item.finalCustomer.payment_iban,
            payment_bic: item.finalCustomer.payment_bic,
            account_owner: item.finalCustomer.account_owner,
            order_blocked: item.finalCustomer.order_blocked,
            order_blocked_reason: item.finalCustomer.order_blocked_reason,
            invoice_delivery_type_id: item.finalCustomer.invoice_delivery_type_id,
            electronic_invoice_id: item.finalCustomer.electronic_invoice_id,
            electronic_invoicing_type: item.finalCustomer.electronic_invoicing_type
        }
    )

    showOneWebshopCustomerAssignDialog.value = false
}

function assignWebglobeCustomerToOrder (item) {
    router.post(
        route('orders.webglobe.assign_customer', {
            orderUuid: item.order.orderNumber,
            customerId: item.customerId
        }),
        {
            customer_type: item.finalCustomer.customer_type,
            brand_id: item.finalCustomer.brand_id,
            title: item.finalCustomer.title,
            firstname: item.finalCustomer.firstname,
            lastname: item.finalCustomer.lastname,
            company: item.finalCustomer.company,
            address1: item.finalCustomer.address1,
            address2: item.finalCustomer.address2,
            postal_code: item.finalCustomer.postal_code,
            city: item.finalCustomer.city,
            country_code: item.finalCustomer.country_code,
            birthday: item.finalCustomer.birthday,
            phone: item.finalCustomer.phone,
            payment_method: item.finalCustomer.payment_method,
            payment_iban: item.finalCustomer.payment_iban,
            payment_bic: item.finalCustomer.payment_bic,
            account_owner: item.finalCustomer.account_owner,
            order_blocked: item.finalCustomer.order_blocked,
            order_blocked_reason: item.finalCustomer.order_blocked_reason,
            invoice_delivery_type_id: item.finalCustomer.invoice_delivery_type_id,
            electronic_invoice_id: item.finalCustomer.electronic_invoice_id,
            electronic_invoicing_type: item.finalCustomer.electronic_invoicing_type
        }
    )

    showWebglobeCustomerAssignDialog.value = false
}

// Note
const selectedItem = ref({})
const showNoteCreateModal = ref(false)
const orderNoteDialog = ref({
    currentNote: {}
})

const showOrderNoteDialog = (order) => {
    selectedItem.value = order
    showNoteCreateModal.value = true

    orderNoteDialog.value.currentNote = order.internalNote ?? {
        id: null, // new note
        order_id: order.idExternal,
        oder_no: order.orderNumber,
        note: ''
    }
}

const saveOrderNote = (form) => {
    const requestMethod = form.id !== null ? 'patch' : 'post'
    const routeName = form.id !== null ? 'ordernotes.update' : 'ordernotes.create'
    const routeParameters = form.id !== null
        ? {
            orderClient: selectedItem.value.client,
            orderNote: form.id,
            _query: { page: props.page }
        }
        : {
            orderClient: selectedItem.value.client,
            orderId: selectedItem.value.idExternal,
            orderNumber: selectedItem.value.orderNumber,
            _query: { page: props.page }
        }

    form[requestMethod](route(routeName, routeParameters), {
        onSuccess: () => {
            showNoteCreateModal.value = false
        }
    })
}

// PSA
const databaseCustomers = ref([])

function checkPsaOrder (item) {
    router.get(
        route('orders.psa.automatic-checks', {
            order: item.orderNumber,
            client: item.client
        })
    )
}

// WooCommerce
const showWooCommerceAcceptDialog = ref(false)
const showWooCommerceRejectDialog = ref(false)
const showWooCommerceAssignCustomerDialog = ref(false)

let acceptWooCommerceCallback = () => {}
let rejectWooCommerceCallback = () => {}

function rejectWooCommerceOrder (order, silent) {
    const orderNumber = order.orderNumber
    dialogTitle.value = __('orders.reject_order_dialog_title', { orderNumber })
    dialogMessage.value = __('orders.reject_dialog_message', { orderNumber })
    rejectWooCommerceCallback = () => {
        router.patch(route('orders.woocommerce.reject-order', { order: order.idExternal, _query: { page: props.page, filter: props.orderData.status } }), { silent: silent })
    }
    showWooCommerceRejectDialog.value = true
}

function itemWooCommerceAcceptable (order) {
    // TODO Check with Sonja if there are others acceptable statuses
    const acceptableStatuses = ['processing']

    if (acceptableStatuses.includes(order.status) && order.valid) {
        return true
    }
}

// For WooCommerce, customer assign process is a part of order accept.
// We will show customer assign dialog if there are matching ones and get data from it or create new customer
function prepareWooCommerceDataForOrderAccept (order) {
    selectedItem.value = order

    if (order.databaseCustomers.length > 0) {
        databaseCustomers.value = order.databaseCustomers
        showWooCommerceAssignCustomerDialog.value = true
    } else if (order.domains.length > 0) {
        showDomainsModal.value = true
    } else {
        const orderNumber = order.orderNumber
        dialogTitle.value = __('orders.accept_order_dialog_title', { orderNumber })
        dialogMessage.value = __('orders.accept_dialog_message', { orderNumber })
        showWooCommerceAcceptDialog.value = true

        acceptWooCommerceCallback = () => {
            acceptWooCommerceOrder()
        }
    }
}

// processing -> completed status
function acceptWooCommerceOrder () {
    router.post(
        route('orders.woocommerce.accept-order', {
            order: selectedItem.value.idExternal
        }),
        {
            customer_id: customerId.value,
            domains: domainsData.value
        },
        {
            onSuccess: () => {
                showDomainsModal.value = false
                showWooCommerceAcceptDialog.value = false
            },
            onError: (errors) => {
                domainErrors.value = errors
            }
        }
    )
}

const customerId = ref(null)
const domainErrors = ref(null)

function assignWooCommerceCustomerAndAccept (customer) {
    showWooCommerceAssignCustomerDialog.value = false
    customerId.value = customer

    if (props.orderData.domains?.length > 0) {
        showDomainsModal.value = true
        return
    }

    acceptWooCommerceOrder()
}

// WooCommerce domains
const showDomainsModal = ref(false)

const domainsData = ref([])

function closeDomainsModal () {
    showDomainsModal.value = false
}

function getDomainOwnerDetails (domains) {
    domainsData.value = Object.values(domains)

    acceptWooCommerceOrder()
}

// End WooCommerce

function updateList (list) {
    columns.value = list
}

const columns = ref([])

setColumns()

function setColumns () {
    for (const userList of props.userListView) {
        columns.value.push({
            key: userList.order_column_no,
            list: userList.list,
            column: userList.column,
            width: userList.width,
            show_column: userList.show_column,
            sort_column_enabled: userList.sort_column_enabled,
            sort_column_supported: userList.sort_column_supported,
            order_column_no: userList.order_column_no
        })
    }
}

function showSectionData (section) {
    for (const userList of props.userListView) {
        if (userList.column === section) {
            return userList.show_column
        }
    }

    return true
}

function isPrepaidOrderWithInvoicingDisabled (order) {
    return order.prepaid === true &&
        (order.customer.billing_type === 'no-invoice' || order.customer.brand.disable_invoicing === true)
}
</script>

<style lang="scss" scoped>
.order-detail {
    display: grid;
    grid-gap: 15px;

    #raw-order-contents {
        width: 100%;
        height: 40em;
        white-space: pre; // do not break on long lines but scroll
    }

    & main {
        display: grid;
        grid-gap: 15px;

        & .d-form-row {
            margin-bottom: 0 !important;
            grid-gap: 0 15px !important;
        }
    }
}
.orders-blocked-text {
    color: var(--colorDanger);
    font-weight: bold;
}
</style>
